import { useTranslation, Trans } from 'react-i18next';
import { Text, Stack, Button, CodeBlock, Loader } from '@hotjar/hotsauce';
import { useCurrentSite, useSiteName } from '@hotjar/site';
import { getTrackingCode } from '@hotjar/webapp/features/Activation/TrackingCodeSnippet/trackingCode';
import { useModalTrackingCode } from '@hotjar/webapp/hooks/useModalTrackingCode';
import { guidesByPlatform } from '@hotjar/webapp/constants/integration-guides';
import { useTrack } from '@hotjar/webapp/hooks/useTrack';
import { useHelpHotjarUrl } from '@hotjar/translations';
import { List, ListItem } from '../components/StepsList';

interface IntegrateWithCMSProps {
  cms: string;
}

export const IntegrateWithCMS = ({ cms }: IntegrateWithCMSProps): JSX.Element => {
  const { t } = useTranslation();
  const helpHotjarUrl = useHelpHotjarUrl();
  const { data: currentSite, isLoading } = useCurrentSite();
  const { show: showTrackingCodeModal } = useModalTrackingCode();
  const track = useTrack();
  const url = `${helpHotjarUrl}${guidesByPlatform[cms]?.url}`;
  const siteName = useSiteName(currentSite);
  const isWordpress = cms === 'wordpress';
  const onVerificationClick = () => {
    track.click(`${cms}`, 'Type of Installation', {});

    if (currentSite) {
      showTrackingCodeModal(currentSite, 2, 'Setup Hotjar Component');
    }
  };

  if (!currentSite || isLoading) {
    return <Loader isLoading />;
  }

  return (
    <List>
      {isWordpress ? (
        <>
          <ListItem>
            <Stack spacing="x4">
              <Stack direction="column">
                <Text textType="body1">
                  {t('activation:setup-wizard.specific-integration.wordpress.add-plugin')}
                </Text>
                <div>
                  <Button as="a" href="https://wordpress.org/plugins/hotjar/" isExternal>
                    {t('activation:setup-wizard.specific-integration.wordpress.get-plugin')}
                  </Button>
                </div>
              </Stack>
            </Stack>
          </ListItem>
          <ListItem>
            <Stack spacing="x4">
              <Text textType="body1">
                <Trans i18nKey="activation:setup-wizard.specific-integration.wordpress.enter-site-id">
                  Enter your site ID: <strong>{{ siteId: currentSite?.id }}</strong> during the
                  checkout process.
                </Trans>
              </Text>
            </Stack>
          </ListItem>
        </>
      ) : (
        <>
          <ListItem>
            <Stack spacing="x4">
              <Stack direction="column">
                <Text textType="body1">
                  {t('activation:setup-wizard.specific-integration.get-tc')}
                </Text>
                <CodeBlock allowCopy content={getTrackingCode(siteName, currentSite?.id)} />
              </Stack>
            </Stack>
          </ListItem>
          <ListItem>
            <Stack spacing="x4">
              <Stack direction="column">
                <Text textType="body1">
                  {t('activation:setup-wizard.specific-integration.follow-guide')}
                </Text>
                <div>
                  <Button as="a" kind="secondary" href={url} isExternal>
                    {t('activation:setup-wizard.specific-integration.view-guide')}
                  </Button>
                </div>
              </Stack>
            </Stack>
          </ListItem>
        </>
      )}
      <ListItem>
        <Stack spacing="x4">
          <Stack direction="column">
            <Text textType="body1">
              {t('activation:setup-wizard.specific-integration.verify-text')}
            </Text>
            <div>
              <Button onClick={onVerificationClick} kind="secondary">
                {t('activation:setup-wizard.integrations.verify-cta')}
              </Button>
            </div>
          </Stack>
        </Stack>
      </ListItem>
    </List>
  );
};
